// frontend/src/contexts/PriceContext.jsx
// Hybrid Pi/USD price:
//   1. Backend /api/rate fetched immediately + polled — guaranteed floor,
//      works on slow connections and in regions where OKX is blocked.
//   2. OKX WebSocket as enhancement — when connected, its real-time ticks
//      take over and polling pauses. On disconnect, polling resumes.
//
// Guarantees:
//   - `loading` only becomes false once a REAL market rate has been shown
//     (from backend, WebSocket, or a recent locally-stored rate). The
//     hardcoded fallback constant never silently unblocks the buy UI.
//   - Last real rate persists to localStorage so returning users start
//     from a true price instead of a stale constant.
//   - On app foreground (visibilitychange), the rate refreshes immediately
//     instead of waiting for the next poll.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from 'react';
import { API_BASE } from '../lib/api';

const PriceContext = createContext();

// Display-only fallback if no real rate has EVER been seen on this device.
// Never unblocks the UI on its own (loading stays true until a real rate).
const FALLBACK_PI_USD = 0.20;
const POLL_INTERVAL_MS = 10000;   // backend poll cadence while WS is down
const RETRY_DELAY_MS = 5000;      // backend retry after a failed fetch
const RECONNECT_DELAY = 5000;     // WS reconnect delay
const WS_URL = 'wss://ws.okx.com:8443/ws/v5/public';

const STORAGE_KEY = 'artibytes_pi_rate_v1';
const STORED_RATE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // accept stored rate up to 24h old

function loadStoredRate() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const price = Number(data?.price);
    const ts = Number(data?.ts);
    if (Number.isNaN(price) || price <= 0) return null;
    if (Number.isNaN(ts) || Date.now() - ts > STORED_RATE_MAX_AGE_MS) return null;
    return price;
  } catch {
    return null;
  }
}

function storeRate(price) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ price, ts: Date.now() }));
  } catch {}
}

export function PriceProvider({ children }) {
  // Start from a recent real rate if this device has one; else the fallback
  // constant is displayed but the UI stays in loading state.
  const storedRate = loadStoredRate();

  const [piPrice, setPiPrice] = useState(storedRate ?? FALLBACK_PI_USD);
  const [loading, setLoading] = useState(storedRate == null);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false); // true = live WS ticks

  const wsRef = useRef(null);
  const wsConnectedRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const hasRealRateRef = useRef(storedRate != null);

  useEffect(() => {
    mountedRef.current = true;

    // Floor first: backend rate enables the UI within one round-trip
    fetchRate();
    // Enhancement second: live ticks if OKX is reachable from this client
    connectWebSocket();

    // Gap 2 fix: refresh immediately when the app returns to foreground.
    // Mobile browsers throttle timers and kill sockets in background; this
    // makes the displayed rate current the moment the user comes back.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && mountedRef.current) {
        if (!wsConnectedRef.current) fetchRate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', onVisible);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  // Single path for applying a genuine market rate from any source
  function applyRealRate(price) {
    hasRealRateRef.current = true;
    setPiPrice(price);
    setLoading(false);
    setError(null);
    storeRate(price);
  }

  /* ---------------- Backend polling (floor) ---------------- */

  async function fetchRate() {
    // While the WS is delivering ticks, polling stays paused
    if (wsConnectedRef.current) return;

    try {
      const res = await fetch(`${API_BASE}/api/rate`, {
        method: 'GET',
        credentials: 'omit'
      });
      if (!res.ok) throw new Error(`rate endpoint ${res.status}`);

      const data = await res.json();
      const price = Number(data.piUsd);

      if (!mountedRef.current) return;

      if (!Number.isNaN(price) && price > 0 && !wsConnectedRef.current) {
        applyRealRate(price);
      }
      schedulePoll(POLL_INTERVAL_MS);
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn('[rate] backend fetch failed:', err?.message);
      if (!wsConnectedRef.current) {
        setError('Rate temporarily unavailable');
        // Only unblock the UI if a real rate has been shown at some point;
        // never let the hardcoded fallback enable the buy flow on its own.
        if (hasRealRateRef.current) setLoading(false);
      }
      schedulePoll(RETRY_DELAY_MS);
    }
  }

  function schedulePoll(delay) {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(() => {
      if (mountedRef.current && !wsConnectedRef.current) fetchRate();
    }, delay);
  }

  /* ---------------- OKX WebSocket (enhancement) ---------------- */

  function connectWebSocket() {
    try {
      console.log('[OKX] Connecting to WebSocket...');
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;

        console.log('[OKX] WebSocket connected');

        // Subscribe to PI-USDT trades (true last price)
        const subscribeMsg = {
          op: 'subscribe',
          args: [
            {
              channel: 'trades',
              instId: 'PI-USDT'
            }
          ]
        };

        ws.send(JSON.stringify(subscribeMsg));
        console.log('[OKX] Subscribed to PI-USDT trades');
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;

        try {
          const data = JSON.parse(event.data);

          // Subscription confirmation
          if (data.event === 'subscribe') {
            console.log('[OKX] Subscription confirmed');
            return;
          }

          // Trade data (real-time last price)
          if (
            data.arg &&
            data.arg.channel === 'trades' &&
            data.data &&
            data.data[0]
          ) {
            const trade = data.data[0];
            const price = parseFloat(trade.px);

            if (!Number.isNaN(price) && price > 0) {
              // First tick received → WS takes over, polling pauses
              if (!wsConnectedRef.current) {
                wsConnectedRef.current = true;
                setConnected(true);
                if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
                console.log('[OKX] Live ticks active — polling paused');
              }
              applyRealRate(price);
            }
          }
        } catch (err) {
          console.error('[OKX] Error parsing message:', err);
        }
      };

      ws.onerror = (err) => {
        if (!mountedRef.current) return;
        console.warn('[OKX] WebSocket error:', err);
        // No setError here — the backend poll keeps the rate flowing
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;

        console.log('[OKX] WebSocket closed');
        const wasLive = wsConnectedRef.current;
        wsConnectedRef.current = false;
        setConnected(false);

        // WS down → polling resumes as the source
        if (wasLive) {
          console.log('[OKX] Falling back to backend polling');
          fetchRate();
        }

        // Attempt reconnection
        reconnectTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            console.log('[OKX] Attempting to reconnect...');
            connectWebSocket();
          }
        }, RECONNECT_DELAY);
      };
    } catch (err) {
      console.warn('[OKX] Failed to connect:', err);
      wsConnectedRef.current = false;
      setConnected(false);
      // Backend polling already running — just retry the WS later
      reconnectTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          connectWebSocket();
        }
      }, RECONNECT_DELAY);
    }
  }

  const value = {
    piPrice,
    loading,
    error,
    connected,

    // Utility: Pi → USD
    piToUsd: (piAmount) => piAmount * piPrice,

    // Utility: USD → Pi
    usdToPi: (usdAmount) => usdAmount / piPrice
  };

  return (
    <PriceContext.Provider value={value}>
      {children}
    </PriceContext.Provider>
  );
}

// Hook to use Pi price in components
export function usePiPrice() {
  const context = useContext(PriceContext);
  if (!context) {
    throw new Error('usePiPrice must be used within PriceProvider');
  }
  return context;
}

// Utility function to format Pi amount
export function formatPi(amount) {
  if (typeof amount !== 'number') return '0 π';
  return `${amount.toFixed(6)} π`;
}

// Utility function to format USD amount
export function formatUsd(amount) {
  if (typeof amount !== 'number') return '$0.00';
  return `$${amount.toFixed(2)}`;
}